import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('loading');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6fcd09ghdq4qda6vl0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#184187] to-[#2563a8]">
        <div className="container mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">تواصل معنا</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
              نحن هنا للإجابة على استفساراتك ومساعدتك
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-xl">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">أرسل لنا رسالة</h2>
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-2">
                    الاسم الكامل
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-base"
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-2">
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-base"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-lg font-semibold text-gray-700 mb-2">
                    رقم الجوال
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-base"
                    placeholder="05xxxxxxxx"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-lg font-semibold text-gray-700 mb-2">
                    الرسالة
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all resize-none text-base"
                    placeholder="اكتب رسالتك هنا (حد أقصى 500 حرف)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">
                    {formData.message.length}/500 حرف
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="w-full bg-[#184187] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#0f2d6b] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitStatus === 'loading' ? 'جاري الإرسال...' : 'إرسال الرسالة'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-center">
                    تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-center">
                    حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">معلومات التواصل</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">البريد الإلكتروني</h3>
                      <a
                        href="mailto:b.alqabbani@sammaty.com"
                        className="text-lg text-gray-600 hover:text-[#184187] transition-colors cursor-pointer"
                      >
                        b.alqabbani@sammaty.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">الهاتف</h3>
                      <a
                        href="tel:0552007272"
                        className="text-lg text-gray-600 hover:text-[#184187] transition-colors cursor-pointer"
                      >
                        0552007272
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">العنوان</h3>
                      <p className="text-lg text-gray-600">MPS-Clinic : Sammaty</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-6">تابعنا على</h3>
                <div className="flex gap-4">
                  <a
                    href="#"
                    className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center text-white hover:bg-[#0f2d6b] transition-colors cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-2xl"></i>
                  </a>
                  <a
                    href="#"
                    className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center text-white hover:bg-[#0f2d6b] transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-line text-2xl"></i>
                  </a>
                  <a
                    href="#"
                    className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center text-white hover:bg-[#0f2d6b] transition-colors cursor-pointer"
                  >
                    <i className="ri-linkedin-fill text-2xl"></i>
                  </a>
                  <a
                    href="#"
                    className="w-14 h-14 bg-[#184187] rounded-lg flex items-center justify-center text-white hover:bg-[#0f2d6b] transition-colors cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-2xl"></i>
                  </a>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">ساعات العمل</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg text-gray-700 font-semibold">السبت - الخميس</span>
                    <span className="text-lg text-gray-600">9:00 ص - 9:00 م</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg text-gray-700 font-semibold">الجمعة</span>
                    <span className="text-lg text-gray-600">مغلق</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">موقعنا</h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.9472447916665!2d46.6752957!3d24.7135517!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzQ4LjgiTiA0NsKwNDAnMzEuMSJF!5e0!3m2!1sar!2ssa!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="موقع سماعتي"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
